import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import ImageUpload from "src/Components/GlobalComponents/ImageUplaod";
import PrimaryBtn from "src/Components/GlobalComponents/primaryBtn";
import httpRequest from "src/helpers/httpRequest";
import { IHomePageInterface, ITagsItems } from "src/types/GlobalInterfaces";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import { canWrite } from "src/helpers/helperFunctions";
import SelectDropdown from "src/Components/GlobalComponents/SelectDropdown";
import { IPaginated } from "src/types/paginated";
import SimpleInput from "src/Components/SimpleInput";
import CustomWebsiteDropdown from "src/Components/GlobalComponents/Widget/CustomWebsiteDropdown";

interface ProductCategoryType {
  packageCategoryID: string;
  key: string;
}

interface ICategoryResponse {
  documents: any[];
  paginated: IPaginated;
}

const PackageCategorySection: React.FC<{
  allCategories: any[];
  setData: React.Dispatch<React.SetStateAction<IHomePageInterface | null>>;
  data: { heading: string; packages: string[] } | null;
}> = ({ allCategories, setData, data }) => {
  const { userDetails } = useSelector((store: RootState) => store.appReducer);
  const hasWriteAccess = canWrite(userDetails, "platformManagement", false);
  const [isLoader, setIsLoader] = useState<boolean>(false);
  const [heading, setHeading] = useState<string>("");
  const [packageCategory, setPackageCategory] = useState<any[]>([]);
  const [packageCategoryLoader, setPackageCategoryLoader] =
    useState<boolean>(false);
  const [productCategory, setProductCategory] = useState<ProductCategoryType[]>(
    Array(8)
      .fill(null)
      .map((_, index) => ({
        packageCategoryID: "",
        key: `package${String(index + 1).padStart(2, "0")}`,
      }))
  );

  useEffect(() => {
    if (data?.packages) {
      setProductCategory(
        data.packages
          .map((packageID, ind) => ({
            packageCategoryID: packageID || "",
            key: `package${String(ind + 1).padStart(2, "0")}`,
          }))
          .concat(
            Array(8 - (data.packages.length || 0))
              .fill(null)
              .map((_, ind) => ({
                packageCategoryID: "",
                key: `package${String(data.packages.length + ind + 1).padStart(
                  2,
                  "0"
                )}`,
              }))
          )
      );
      setHeading(data?.heading || "");
    } else {
      setProductCategory(
        Array(8)
          .fill(null)
          .map((_, index) => ({
            packageCategoryID: "",
            key: `package${String(index + 1).padStart(2, "0")}`,
          }))
      );
      setHeading("");
    }
  }, [data]);

  const getAllCategories = async () => {
    setPackageCategoryLoader(true);
    const { res, err } = await httpRequest<any>({
      path: `/diagnostic-package/all?limit=1000`,
    });
    if (res) {
      setPackageCategory(res?.documents);
    } else {
      toast(err, { type: "error" });
    }
    setPackageCategoryLoader(false);
  };

  useEffect(() => {
    getAllCategories();
  }, []);

  const handleSubmit = async () => {
    if (!hasWriteAccess) {
      return;
    }
    if (!heading) {
      toast.error("Heading is required");
      return;
    }
    if (productCategory.some((tm) => !tm?.packageCategoryID)) {
      toast.error("Please select all package categories");
      return;
    }
    setIsLoader(true);
    let { res, err } = await httpRequest({
      path: "/website-manage/section-eight",
      method: "put",
      params: {
        heading,
        packages: productCategory?.map((tm) => tm?.packageCategoryID),
      },
    });
    if (res) {
      toast.success("Package category updated successfully");
    } else {
      toast.error(err);
    }
    setIsLoader(false);
  };

  const CheckDuplicates = (tm: string, index: number) => {
    let allData = productCategory.filter((t, i) => i !== index);
    return allData.find((t) => t.packageCategoryID === tm) ? true : false;
  };

  return (
    <div className="section__1">
      <h1 className="font-Poppins-SemiBold font-size-25px mb-2 theme-color-green">
        Section 08
      </h1>
      <div className="products-tabs-content">
        <div>
          <h1 className="font-size-18px text-[#2F2F31] mb-3 font-Poppins-Regular">
            Heading
          </h1>
          <SimpleInput
            labelText="Heading"
            placeholder="Type Heading"
            value={heading}
            onChange={(value: string) => {
              console.log("Heading", value);
              setHeading(value || "");
            }}
            id="package-category-heading"
            disabled={!hasWriteAccess}
          />
          <div className="w-full grid grid-cols-4 gap-4 gap-x-10">
            {productCategory.map((tm, ind) => {
              return (
                <div className="w-full" key={tm.key}>
                  <h1 className="font-size-18px text-[#2F2F31] mb-3 font-Poppins-Regular">
                    Product {String(ind + 1).padStart(2, "0")}
                  </h1>
                  <CustomWebsiteDropdown
                    loading={packageCategoryLoader}
                    options={packageCategory.map((tm) => ({
                      label: tm.name,
                      value: tm._id,
                      disabled: CheckDuplicates(tm._id, ind),
                    }))}
                    data={
                      tm?.packageCategoryID
                        ? [
                            {
                              label:
                                packageCategory.find(
                                  (pkg) => pkg._id === tm?.packageCategoryID
                                )?.name || "",
                              value: tm?.packageCategoryID,
                            },
                          ]
                        : []
                    }
                    setData={(val) => {
                      setProductCategory((prev) => {
                        const newBanner = [...prev];
                        newBanner[ind] = {
                          ...newBanner[ind],
                          packageCategoryID: val[0]?.value || "",
                        };
                        return newBanner;
                      });
                    }}
                    placeholder="Select Package Category"
                    dropdownMainId={`package-category-${ind + 1}`}
                    disabled={!hasWriteAccess}
                  />
                </div>
              );
            })}
          </div>
        </div>
      </div>
      <div className="flex mt-2 justify-end">
        <PrimaryBtn
          disabled={
            typeof productCategory === "object"
              ? false
              : true || !hasWriteAccess
          }
          loader={isLoader}
          ButtonText="Save"
          btnExtraStyle={{
            backgroundColor: "#00443f",
            borderRadius: "1.5rem",
          }}
          onPress={() => {
            if (!hasWriteAccess) {
              return;
            }
            handleSubmit();
          }}
          btnExtralClasses={`${!hasWriteAccess ? "cursor-not-allowed" : ""}`}
          id="package-category-save"
        />
      </div>
    </div>
  );
};

export default PackageCategorySection;
